import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Newspaper, RefreshCw, Loader2, Sparkles, CalendarDays } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { PageHeader, Markdown, EmptyState, Panel } from "@/components/common";
import { api, errMsg, localToday, fmtDate, fmtDateTime } from "@/lib/api";

export default function Briefing() {
  const navigate = useNavigate();
  const [date, setDate] = useState(localToday());
  const [data, setData] = useState(null);
  const [generating, setGenerating] = useState(false);

  const load = useCallback(async (d) => {
    setData(null);
    try {
      const { data } = await api.get("/briefing", { params: { date: d } });
      setData(data);
    } catch (e) {
      toast.error(errMsg(e));
      setData({ briefing: null, history: [] });
    }
  }, []);

  useEffect(() => { load(date); }, [date, load]);

  const generate = async () => {
    setGenerating(true);
    try {
      const { data: b } = await api.post("/briefing/generate", { date });
      setData((d) => ({ briefing: b, history: d.history.some((h) => h.date === b.date) ? d.history : [{ date: b.date, generated_at: b.generated_at }, ...d.history] }));
      toast.success("Briefing ready");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setGenerating(false);
    }
  };

  const isToday = date === localToday();
  const b = data?.briefing;

  return (
    <div data-testid="briefing-page">
      <PageHeader
        title="Daily CEO briefing"
        subtitle={fmtDate(date, { weekday: "long", month: "long", day: "numeric", year: "numeric" })}
        actions={<>
          {!isToday && <Button variant="secondary" onClick={() => setDate(localToday())} data-testid="briefing-today-button">Back to today</Button>}
          <Button onClick={generate} disabled={generating} data-testid="briefing-generate-button">
            {generating ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : b ? <RefreshCw className="h-4 w-4 mr-1.5" /> : <Sparkles className="h-4 w-4 mr-1.5" />}
            {generating ? "Writing briefing…" : b ? "Regenerate" : "Generate briefing"}
          </Button>
        </>}
      />
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <section className="lg:col-span-9 bg-card border border-border rounded-xl p-5 sm:p-7" data-testid="briefing-content">
          {data === null || generating ? (
            <div className="space-y-3">
              {generating && <p className="text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" />Your CEO is reading today's data and writing the briefing. This usually takes 20–40 seconds.</p>}
              <Skeleton className="h-6 w-1/2" /><Skeleton className="h-4 w-full" /><Skeleton className="h-4 w-5/6" /><Skeleton className="h-4 w-2/3" /><Skeleton className="h-24 w-full" />
            </div>
          ) : b ? (
            <>
              <div className="flex flex-wrap gap-2 mb-5">
                {[["Meetings", b.kpis?.meetings_today], ["Overdue", b.kpis?.overdue_tasks], ["Pending leave", b.kpis?.pending_leave], ["Headcount", b.kpis?.headcount]].map(([l, v]) => (
                  <span key={l} className="text-xs bg-muted rounded-full px-3 py-1"><span className="text-muted-foreground">{l}</span> <span className="font-semibold tabular-nums">{v ?? 0}</span></span>
                ))}
              </div>
              <Markdown>{b.content}</Markdown>
              <div className="mt-6 pt-4 border-t border-border flex flex-wrap items-center justify-between gap-3">
                <p className="text-xs text-muted-foreground">Generated {fmtDateTime(b.generated_at)}{b.generated_by ? ` for ${b.generated_by}` : ""}</p>
                <Button size="sm" variant="secondary" onClick={() => navigate("/ai-ceo?q=Based on today's briefing, propose the actions I should approve now")} data-testid="briefing-act-button">
                  <Sparkles className="h-4 w-4 mr-1.5 text-primary" />Turn into actions
                </Button>
              </div>
            </>
          ) : (
            <EmptyState icon={Newspaper} title={isToday ? "No briefing yet today" : "No briefing for this day"}
              text="Your CEO will pull today's meetings, overdue work, leave and goals into a one-page summary."
              action={<Button onClick={generate} data-testid="briefing-empty-generate-button"><Sparkles className="h-4 w-4 mr-1.5" />Generate briefing</Button>} />
          )}
        </section>
        <Panel title="Past briefings" icon={CalendarDays} className="lg:col-span-3 h-fit" testid="briefing-history">
          {!data?.history?.length ? <p className="text-xs text-muted-foreground">None yet.</p> : (
            <ul className="space-y-1">
              {data.history.map((h) => (
                <li key={h.date}>
                  <button onClick={() => setDate(h.date)} data-testid="briefing-history-item"
                    className={`w-full text-left text-sm rounded-lg px-2.5 py-2 transition-colors ${h.date === date ? "bg-muted font-medium" : "hover:bg-muted/70"}`}>
                    {fmtDate(h.date, { weekday: "short", month: "short", day: "numeric" })}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>
    </div>
  );
}
